import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Confirm,
  Header,
  Icon,
  Label,
  Message,
  Segment,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';
import './BillingPriceImportV82.css';
import {
  buildBillingPriceImportDecisionsV814,
  summarizeBillingPriceImportSelectionsV814,
} from './billingPriceImportV814';

const ImportSummary = ({ preview, t }) => (
  <div className='billing-import-summary-v82'>
    <Label basic>
      {t('setting.billing.import.summary.models')}: {preview.model_count}
    </Label>
    <Label color='green' basic>
      {t('setting.billing.import.summary.importable')}:{' '}
      {preview.importable_count}
    </Label>
    <Label color='blue' basic>
      {t('setting.billing.import.summary.existing')}:{' '}
      {preview.existing_profile_count}
    </Label>
    <Label color={preview.ignored_count ? 'orange' : undefined} basic>
      {t('setting.billing.import.summary.ignored')}: {preview.ignored_count}
    </Label>
    <Label color={preview.warning_count ? 'orange' : undefined} basic>
      {t('setting.billing.import.summary.warnings')}: {preview.warning_count}
    </Label>
  </div>
);

const formatAttributesV814 = (attributes) => {
  const entries = Object.entries(attributes || {});
  if (!entries.length) return '-';
  return entries.map(([key, value]) => `${key}: ${String(value)}`).join(' | ');
};

const ImportProfileV814 = ({ profile, title, emptyText, t }) => (
  <section className='billing-import-profile-v814'>
    <h5>{title}</h5>
    {!profile ? (
      <div className='billing-import-empty-profile-v814'>{emptyText}</div>
    ) : (
      <>
        <div className='billing-import-profile-meta-v814'>
          {profile.config_version ? (
            <span>
              {t('setting.billing.import.comparison.version')}:{' '}
              {profile.config_version}
            </span>
          ) : null}
          <span>
            {t('setting.billing.import.preview.effective_at')}:{' '}
            {new Date(profile.effective_at * 1000).toLocaleString()}
          </span>
        </div>
        <div className='billing-import-capabilities-v814'>
          {(profile.capabilities || []).map((capability) => (
            <Label key={capability} basic size='tiny'>
              {capability}
            </Label>
          ))}
        </div>
        {(profile.components || []).length ? (
          <div className='billing-import-price-scroll-v814'>
            <Table compact unstackable size='small'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {t('models.commercial.meter')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('setting.billing.import.comparison.unit')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('models.commercial.price')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('setting.billing.import.comparison.attributes')}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {profile.components.map((component, index) => (
                  <Table.Row
                    key={`${component.meter}-${JSON.stringify(
                      component.attributes || {}
                    )}-${index}`}
                  >
                    <Table.Cell>
                      {t(`models.commercial.meters.${component.meter}`, {
                        defaultValue: component.meter,
                      })}
                    </Table.Cell>
                    <Table.Cell>{component.unit}</Table.Cell>
                    <Table.Cell>{component.unit_amount_usd}</Table.Cell>
                    <Table.Cell className='billing-import-attributes-v814'>
                      {formatAttributesV814(component.attributes)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        ) : (
          <div className='billing-import-empty-profile-v814'>
            {t('models.commercial.empty_prices')}
          </div>
        )}
        {profile.note ? (
          <div className='billing-import-profile-note-v814'>
            {t('models.commercial.note')}: {profile.note}
          </div>
        ) : null}
      </>
    )}
  </section>
);

const BillingPriceImportV82 = ({ onImported }) => {
  const { t } = useTranslation();
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [decisions, setDecisions] = useState({});

  const loadHistory = useCallback(async () => {
    try {
      const response = await API.get(
        '/api/billing/v8/admin/price-imports?page=1&page_size=20'
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      setHistory(response.data.data?.items || []);
      setHistoryTotal(response.data.data?.total || 0);
    } catch (error) {
      showError(error.message || t('setting.billing.import.errors.history'));
    }
  }, [t]);

  useEffect(() => {
    loadHistory().then();
  }, [loadHistory]);

  const selectFile = (event) => {
    const selected = event.target.files?.[0] || null;
    setFile(selected);
    setPreview(null);
    setDecisions({});
    setConfirmOpen(false);
  };

  const previewFile = async () => {
    if (!file) {
      showError(t('setting.billing.import.errors.file_required'));
      return;
    }
    setPreviewing(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const response = await API.post(
        '/api/billing/v8/admin/price-imports/preview',
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      const nextPreview = response.data.data;
      setPreview(nextPreview);
      setDecisions(buildBillingPriceImportDecisionsV814(nextPreview));
      await loadHistory();
      showSuccess(t('setting.billing.import.messages.previewed'));
    } catch (error) {
      setPreview(null);
      showError(error.message || t('setting.billing.import.errors.preview'));
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    if (!preview?.preview_token || preview.already_imported) return;
    setConfirmOpen(false);
    setCommitting(true);
    try {
      const response = await API.post(
        '/api/billing/v8/admin/price-imports/commit',
        { preview_token: preview.preview_token, decisions }
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      showSuccess(
        response.data.data?.already_imported
          ? t('setting.billing.import.messages.already_imported')
          : t('setting.billing.import.messages.imported')
      );
      setPreview((current) =>
        current
          ? {
              ...current,
              already_imported: true,
              imported_at: response.data.data?.imported_at,
            }
          : current
      );
      await Promise.all([loadHistory(), onImported?.()]);
    } catch (error) {
      showError(error.message || t('setting.billing.import.errors.commit'));
    } finally {
      setCommitting(false);
    }
  };

  const selectionSummary = summarizeBillingPriceImportSelectionsV814(
    preview,
    decisions
  );

  const setModelDecision = (modelName, decision) => {
    setDecisions((current) => ({ ...current, [modelName]: decision }));
  };

  return (
    <Segment className='billing-price-import-v82'>
      <Header as='h3'>
        <Icon name='file code outline' />
        <Header.Content>
          {t('setting.billing.import.title')}
          <Header.Subheader>
            {t('setting.billing.import.subtitle')}
          </Header.Subheader>
        </Header.Content>
      </Header>
      <Message info>
        <Message.Header>
          {t('setting.billing.import.protocol_title')}
        </Message.Header>
        <p>{t('setting.billing.import.protocol_description')}</p>
      </Message>

      <div className='billing-import-picker-v82'>
        <input
          ref={fileInput}
          type='file'
          accept='.json,application/json'
          onChange={selectFile}
          aria-label={t('setting.billing.import.actions.select_file')}
        />
        <Button
          type='button'
          icon
          labelPosition='left'
          loading={previewing}
          disabled={!file || committing}
          onClick={previewFile}
        >
          <Icon name='search' />
          {t('setting.billing.import.actions.preview')}
        </Button>
      </div>
      {file ? (
        <div className='billing-import-file-v82'>
          <strong>{file.name}</strong>
          <span>{Math.max(1, Math.ceil(file.size / 1024))} KB</span>
        </div>
      ) : null}

      {preview ? (
        <div className='billing-import-preview-v82'>
          <Header as='h4'>{t('setting.billing.import.preview.title')}</Header>
          <div className='billing-import-metadata-v82'>
            <span>
              {t('setting.billing.import.preview.schema')}:{' '}
              <strong>{preview.schema_version}</strong>
            </span>
            <span>
              {t('setting.billing.import.preview.effective_at')}:{' '}
              <strong>
                {new Date(preview.effective_at * 1000).toLocaleString()}
              </strong>
            </span>
            <span>
              SHA256: <code>{preview.file_sha256}</code>
            </span>
          </div>
          <ImportSummary preview={preview} t={t} />
          {(preview.warnings || []).map((warning) => (
            <Message key={warning} warning content={warning} />
          ))}
          <div className='billing-import-selection-summary-v814'>
            <Label color='green' basic>
              {t('setting.billing.import.selection.replace')}:{' '}
              {selectionSummary.replace}
            </Label>
            <Label basic>
              {t('setting.billing.import.selection.keep')}:{' '}
              {selectionSummary.keep}
            </Label>
          </div>
          <div className='billing-import-model-list-v814'>
            {(preview.items || []).map((item) => (
              <article
                key={item.model_name}
                className={`billing-import-model-v814${
                  item.ignored ? ' is-ignored' : ''
                }`}
              >
                <div className='billing-import-model-header-v814'>
                  <div>
                    <strong>{item.model_name}</strong>
                    <div className='billing-muted-v8'>{item.model_type}</div>
                  </div>
                  {item.ignored ? (
                    <Label color='orange' basic>
                      {t('setting.billing.import.selection.ignored')}
                    </Label>
                  ) : item.requires_decision ? (
                    <Button.Group size='small'>
                      <Button
                        type='button'
                        active={decisions[item.model_name] === 'keep'}
                        aria-pressed={decisions[item.model_name] === 'keep'}
                        onClick={() =>
                          setModelDecision(item.model_name, 'keep')
                        }
                      >
                        {t('setting.billing.import.selection.keep')}
                      </Button>
                      <Button
                        type='button'
                        color={
                          decisions[item.model_name] === 'replace'
                            ? 'blue'
                            : undefined
                        }
                        active={decisions[item.model_name] === 'replace'}
                        aria-pressed={decisions[item.model_name] === 'replace'}
                        onClick={() =>
                          setModelDecision(item.model_name, 'replace')
                        }
                      >
                        {t('setting.billing.import.selection.replace')}
                      </Button>
                    </Button.Group>
                  ) : (
                    <Label color='green' basic>
                      {t('setting.billing.import.selection.new_profile')}
                    </Label>
                  )}
                </div>
                {item.ignored ? (
                  <p className='billing-import-ignore-reason-v814'>
                    {t(
                      `setting.billing.import.ignore_reasons.${item.ignore_reason}`,
                      { defaultValue: item.ignore_reason }
                    )}
                  </p>
                ) : (
                  <>
                    <div className='billing-import-cost-profile-v814'>
                      {t('setting.billing.import.preview.profile')}:{' '}
                      <code>{item.cost_profile_key}</code>
                    </div>
                    <div className='billing-import-profile-grid-v814'>
                      <ImportProfileV814
                        profile={item.current_profile}
                        title={t('setting.billing.import.comparison.current')}
                        emptyText={t(
                          'setting.billing.import.comparison.not_configured'
                        )}
                        t={t}
                      />
                      <ImportProfileV814
                        profile={item.imported_profile}
                        title={t('setting.billing.import.comparison.incoming')}
                        emptyText='-'
                        t={t}
                      />
                    </div>
                  </>
                )}
              </article>
            ))}
          </div>
          {preview.already_imported ? (
            <Message
              positive
              content={t('setting.billing.import.already_imported')}
            />
          ) : preview.importable_count > 0 ? (
            <Button
              type='button'
              color='green'
              icon
              labelPosition='left'
              loading={committing}
              disabled={!preview.preview_token}
              onClick={() => setConfirmOpen(true)}
            >
              <Icon name='check circle' />
              {t('setting.billing.import.actions.commit')}
            </Button>
          ) : (
            <Message
              info
              content={t('setting.billing.import.no_importable_models')}
            />
          )}
        </div>
      ) : null}

      <Header as='h4'>{t('setting.billing.import.history.title')}</Header>
      {history.length ? (
        <div className='management-table-scroll-v72' role='region' tabIndex={0}>
          <Table compact unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {t('setting.billing.import.history.file')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('setting.billing.import.preview.schema')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('setting.billing.import.history.status')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('setting.billing.import.summary.models')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('setting.billing.import.history.time')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {history.map((batch) => (
                <Table.Row key={batch.id}>
                  <Table.Cell>
                    <strong>{batch.filename}</strong>
                    <div className='billing-import-hash-v82'>
                      {batch.file_sha256}
                    </div>
                  </Table.Cell>
                  <Table.Cell>{batch.schema_version}</Table.Cell>
                  <Table.Cell>
                    <Label
                      basic
                      color={batch.status === 'imported' ? 'green' : 'blue'}
                    >
                      {batch.status}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>{batch.model_count}</Table.Cell>
                  <Table.Cell>
                    {new Date(
                      (batch.imported_at || batch.created_at) * 1000
                    ).toLocaleString()}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <span className='billing-muted-v8'>
            {t('setting.billing.import.history.total', {
              count: historyTotal,
            })}
          </span>
        </div>
      ) : (
        <Message content={t('setting.billing.import.history.empty')} />
      )}

      <Confirm
        open={confirmOpen}
        header={t('setting.billing.import.confirm.title')}
        content={t('setting.billing.import.confirm.content', {
          filename: preview?.filename || '',
          replacement_count: selectionSummary.replace,
          keep: selectionSummary.keep,
          ignored: selectionSummary.ignored,
        })}
        cancelButton={t('setting.billing.import.confirm.cancel')}
        confirmButton={t('setting.billing.import.confirm.commit')}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={commit}
      />
    </Segment>
  );
};

export default BillingPriceImportV82;
